import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, BriefcaseBusiness, Clock3, HeartHandshake, Lightbulb, Quote, Star, UsersRound } from "lucide-react";

const milestones = [
  ["IT staffing", "Helping businesses meet their IT consulting and staffing needs"],
  ["Wider hiring", "Supporting non-IT roles and bulk recruitment drives"],
  ["Smarter screening", "Combining recruiter expertise with AI-enabled screening"],
  ["Global reach", "Serving clients in India and international markets"],
];

const values = [
  [BadgeCheck, "Integrity First", "We are honest with clients about timelines and with candidates about roles. No false promises, no hidden terms."],
  [Star, "Quality Over Volume", "Every profile we share is screened for skills, experience and cultural fit, so your team spends time interviewing, not filtering."],
  [Clock3, "Speed with Structure", "Urgent requirements or large hiring drives, our process is built to move quickly without cutting corners."],
  [HeartHandshake, "People-Centric Approach", "Behind every requirement is a business goal, and behind every resume is a person's career. We respect both."],
  [Lightbulb, "Continuous Innovation", "We use AI-powered screening and data-driven insights to keep improving hiring outcomes."],
];

const services = [
  ["IT Recruitment", "Developers, cloud, data, DevOps, QA, cybersecurity and tech leadership roles"],
  ["Non-IT Recruitment", "Sales, HR, finance, BFSI, operations, customer support and e-commerce roles"],
  ["Permanent Hiring", "End-to-end sourcing, screening, interview coordination and onboarding"],
  ["Contract & Temporary Staffing", "Flexible workforce solutions for project-based needs"],
  ["Bulk Hiring Drives", "Structured, high-volume recruitment campaigns", "/#bulk-hiring"],
  ["HR Consulting", "Workforce planning, hiring process design and HR support"],
  ["AI & IT Services", "AI-enabled screening and IT consulting to improve hiring quality", "/#ai-services"],
];

const faqs = [
  ["What makes WorkingBees Global different from other staffing agencies in India?", "We combine domain-specialised recruiters with AI-enabled screening, so clients get pre-qualified candidates faster and with better fit."],
  ["Do you hire for both IT and non-IT roles?", "Yes. We cover technology roles as well as sales, HR, BFSI, operations, customer support and e-commerce positions."],
  ["Can you handle large-volume hiring?", "Yes. Our bulk hiring drives are designed for companies that need to fill dozens or hundreds of positions within tight timelines."],
  ["Do you serve clients outside India?", "Yes. We support businesses across India and in the Middle East."],
  ["How do job seekers apply?", "Candidates can browse open roles on our Jobs page and apply directly, or share their resume with our team."],
];

const sectionClass = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8";
const headingClass = "font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight";
const cardClass = "rounded-2xl border border-border bg-card p-6 card-lift";

function AnimatedStat({ value, label }) {
  const element = useRef(null);
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    const match = value.match(/^(\d+)(.*)$/);
    if (!match || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let frame;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      const target = Number(match[1]);
      const started = performance.now();
      const tick = (now) => {
        const progress = Math.min((now - started) / 1100, 1);
        setDisplay(`${Math.round(target * (1 - Math.pow(1 - progress, 3)))}${match[2]}`);
        if (progress < 1) frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    });
    observer.observe(element.current);
    return () => { observer.disconnect(); cancelAnimationFrame(frame); };
  }, [value]);

  return <div ref={element} className="rounded-2xl bg-bee-teal p-6 text-white"><div className="font-display text-4xl font-bold text-bee-honey">{display}</div><div className="mt-2 text-sm font-semibold">{label}</div></div>;
}

export default function AboutUsContent({ initialTestimonials = [] }) {
  const [testimonials, setTestimonials] = useState(initialTestimonials);

  useEffect(() => {
    fetch("/api/testimonials")
      .then((response) => response.ok ? response.json() : Promise.reject(new Error("Testimonials unavailable")))
      .then((items) => setTestimonials(Array.isArray(items) ? items : []))
      .catch(() => {});
  }, []);

  return (
    <main className="pt-[72px]" data-testid="about-us-page">
      <section className="relative overflow-hidden bg-gradient-to-br from-bee-teal to-[hsl(var(--bee-teal-dark))] text-white py-16 lg:py-24">
        <div className="absolute inset-0 honeycomb-divider opacity-25" aria-hidden="true" />
        <div className={`${sectionClass} relative`}>
          <div className="text-xs uppercase tracking-[0.2em] text-bee-honey font-semibold mb-4">About Us</div>
          <h1 className="font-display max-w-5xl text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08]">About WorkingBees Global – A Trusted Recruitment Firm in India</h1>
          <p className="mt-5 text-xl sm:text-2xl text-bee-honey font-semibold max-w-3xl">Connecting ambitious businesses with the talent that moves them forward.</p>
          <div className="mt-8 max-w-4xl space-y-4 text-white/85 leading-relaxed">
            <p>WorkingBees Global Pvt. Ltd. is a recruitment and staffing agency in India that helps organisations hire faster, smarter and with greater confidence. We partner with startups, SMEs and large enterprises to fill critical roles across technology, sales, HR, BFSI, e-commerce and business operations, both in India and in international markets.</p>
            <p>Like the bees we are named after, we believe great results come from focused effort, teamwork and a system that works. That philosophy shapes everything from how we screen candidates to how we manage large-scale hiring drives.</p>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="/#contact" className="rounded-full bg-bee-orange px-6 py-3 text-sm font-bold text-white hover:bg-bee-orange/90 transition">Hire Talent</a>
            <Link to="/jobs" className="rounded-full border border-white/60 px-6 py-3 text-sm font-bold text-white hover:bg-white/10 transition">View Open Jobs</Link>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className={sectionClass}>
          <h2 className={headingClass}>Our <span className="text-bee-teal">Story</span></h2>
          <div className="mt-6 max-w-4xl space-y-4 text-muted-foreground leading-relaxed">
            <p>WorkingBees Global grew from a simple observation: companies were losing time and money on slow, unstructured hiring, while skilled candidates struggled to find the right opportunities.</p>
            <p>We started by helping companies with IT consulting and staffing needs. As our clients grew, so did we, expanding into non-IT hiring, contract staffing, bulk recruitment drives and HR consulting. Today, we combine traditional recruitment expertise with AI-enabled screening to deliver quality hires at speed, serving clients across India, Europe, Asia Pacific and the Middle East.</p>
          </div>
          <ol className="mt-10 max-w-3xl border-l-2 border-bee-teal/30 ml-3 space-y-6">
            {milestones.map(([stage, description]) => <li key={stage} className="relative pl-8"><span className="absolute -left-[9px] top-1 h-4 w-4 rounded-full border-4 border-bee-teal bg-background" /><h3 className="font-display font-bold text-bee-orange">{stage}</h3><p className="mt-1 text-foreground/80">{description}</p></li>)}
          </ol>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-secondary/40">
        <div className={sectionClass}>
          <h2 className={headingClass}>Our Mission &amp; Vision</h2>
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            <article className={cardClass}><h3 className="font-display text-2xl font-bold text-bee-teal">Our Mission</h3><p className="mt-4 text-muted-foreground leading-relaxed">To make hiring simple, fast and reliable for employers, while opening doors to meaningful careers for job seekers across India and beyond.</p></article>
            <article className={cardClass}><h3 className="font-display text-2xl font-bold text-bee-teal">Our Vision</h3><p className="mt-4 text-muted-foreground leading-relaxed">To be the most trusted staffing agency in India for businesses that value speed, quality and long-term workforce success.</p></article>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className={sectionClass}>
          <h2 className={headingClass}>Our Core Values</h2>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {values.map(([Icon, title, description]) => <article key={title} className={cardClass}><span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-bee-teal/10 text-bee-teal"><Icon className="h-5 w-5" aria-hidden="true" /></span><h3 className="mt-4 font-display text-xl font-bold">{title}</h3><p className="mt-3 text-sm text-muted-foreground leading-relaxed">{description}</p></article>)}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-secondary/40">
        <div className={sectionClass}>
          <h2 className={headingClass}>What We Do</h2>
          <p className="mt-4 text-muted-foreground">As a full-service recruitment firm in India, we offer:</p>
          <div className="mt-9 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map(([title, description, href]) => <article key={title} className={cardClass}><span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-bee-orange/10 text-bee-orange"><BriefcaseBusiness className="h-5 w-5" aria-hidden="true" /></span><h3 className="mt-4 font-display text-xl font-bold">{href ? <a className="hover:text-bee-teal" href={href}>{title}</a> : title}</h3><p className="mt-3 text-sm text-muted-foreground leading-relaxed">{description}</p></article>)}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className={sectionClass}>
          <h2 className={headingClass}>Why Choose WorkingBees Global?</h2>
          <div className="mt-9 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[["100+", "Clients served"], ["10+", "Industries covered"], ["20+", "Cities reached"], ["500+", "Candidates placed"]].map(([number, label]) => <AnimatedStat key={label} value={number} label={label} />)}
          </div>
          <ul className="mt-10 grid md:grid-cols-2 gap-4">
            {["Industry-specialised recruiters who understand your domain", "Pre-screened candidates assessed for technical and soft skills", "Pan-India reach with the ability to hire for international roles", "Transparent communication with regular status updates"].map((point) => <li key={point} className="flex items-start gap-3 text-foreground/85"><BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-bee-teal" aria-hidden="true" />{point}</li>)}
          </ul>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-secondary/40">
        <div className={sectionClass}>
          <h2 className={headingClass}>What Our Clients Say</h2>
          <div className="mt-9 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.map((item) => <article key={item.id} className={`${cardClass} relative`}><Quote className="absolute right-5 top-5 h-8 w-8 text-bee-teal/15" aria-hidden="true" /><div className="flex" aria-label={`${item.rating || 5} out of 5 stars`}>{Array.from({ length: item.rating || 5 }, (_, index) => <Star key={index} className="h-4 w-4 fill-bee-orange text-bee-orange" aria-hidden="true" />)}</div><p className="mt-4 text-sm leading-relaxed">&ldquo;{item.quote}&rdquo;</p><div className="mt-5 border-t border-border pt-4"><div className="font-display font-bold">{item.author_name}</div><div className="text-xs text-muted-foreground">{item.author_title}</div></div></article>)}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className={sectionClass}>
          <h2 className={headingClass}>Frequently Asked Questions</h2>
          <div className="mt-9 max-w-4xl space-y-3">
            {faqs.map(([question, answer]) => <details key={question} className="group rounded-2xl border border-border bg-card p-5"><summary className="cursor-pointer text-foreground marker:text-bee-orange"><h3 className="inline font-display font-bold text-lg">{question}</h3></summary><p className="mt-4 text-muted-foreground leading-relaxed">{answer}</p></details>)}
          </div>
        </div>
      </section>
    </main>
  );
}
